/**
 * Frontend Environment Configuration
 * Local Development: Uses localhost:3001 (via .env.local)
 * Vercel Production: Uses Railway backend (via Vercel environment variables)
 */

// Base URLs for different environments
const LOCAL_API_URL = 'http://localhost:3001';  // Fallback for local development
const PRODUCTION_API_URL = 'https://aerosync-backend-production-bf9e.up.railway.app';

// Environment detection
const isProd = import.meta.env.PROD;
const isDev = !isProd;
const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';

// Determine API URL with simple logic
const determineApiUrl = (): string => {
  // Priority 1: Use environment variable if set (this is where .env.local takes effect)
  const envApiUrl = import.meta.env.VITE_API_BASE_URL;
  if (envApiUrl) {
    return envApiUrl;
  }
  
  // Priority 2: Fallback based on environment
  if (isLocalhost && isDev) {
    return LOCAL_API_URL;
  }
  
  // Priority 3: Production fallback
  return PRODUCTION_API_URL;
};

// Export the determined API URL
export const API_URL = determineApiUrl();

// Environment object for convenience
export const ENV = {
  API_URL,
  APP_URL: isLocalhost ? 'http://localhost:5173' : `https://${typeof window !== 'undefined' ? window.location.hostname : ''}`,
  IS_PROD: isProd,
  IS_DEV: isDev,
  IS_LOCALHOST: isLocalhost
} as const;

// Supabase configuration (frontend uses anon key)
// PERMANENT FIX: Enforce correct Supabase project configuration
const CORRECT_SUPABASE_URL = 'https://hpozuqhkxpexrzjysvpo.supabase.co';
const CORRECT_SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imhwb3p1cWhreHBleHJ6anlzdnBvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDU2NTEyOTEsImV4cCI6MjA2MTIyNzI5MX0.griuZzg_krzKEZwrJOt4A44gZouQXRkAQJKe4BK03Vk';

// Validate environment variables against correct values
function validateSupabaseConfig() {
  const envUrl = import.meta.env.VITE_SUPABASE_URL;
  const envKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  // Always use correct values - no environment variable validation needed
  // This ensures production deployment always uses the right configuration
  console.log('🔧 Enforcing correct Supabase configuration (no env var dependency)');
  console.log('📊 Env URL:', envUrl ? 'SET' : 'NOT SET');
  console.log('📊 Env Key:', envKey ? `SET (starts with: ${envKey.substring(0, 10)}...)` : 'NOT SET');
  
  if (envKey && !envKey.startsWith('eyJhbGciOiJIUzI1NiIs')) {
    console.warn('⚠️ Wrong environment variable detected, using hardcoded correct values');
  }
  
  // Always return the correct values regardless of environment variables
  return {
    URL: CORRECT_SUPABASE_URL,
    ANON_KEY: CORRECT_SUPABASE_ANON_KEY
  };
}

export const SUPABASE_CONFIG = validateSupabaseConfig();

// Debug environment variables
if (typeof window !== 'undefined') {
  console.log('🔍 Supabase Config Debug:');
  console.log('URL from env:', import.meta.env.VITE_SUPABASE_URL);
  console.log('URL used:', SUPABASE_CONFIG.URL);
  console.log('Anon key from env:', import.meta.env.VITE_SUPABASE_ANON_KEY ? 'SET' : 'NOT SET');
  console.log('Anon key used (first 20 chars):', SUPABASE_CONFIG.ANON_KEY.substring(0, 20) + '...');
  
  // Verify we're using the correct configuration
  const isCorrectConfig = SUPABASE_CONFIG.URL === CORRECT_SUPABASE_URL && 
                         SUPABASE_CONFIG.ANON_KEY === CORRECT_SUPABASE_ANON_KEY;
  console.log('✅ Using correct Supabase config:', isCorrectConfig);
  console.log('🔑 Correct key starts with:', CORRECT_SUPABASE_ANON_KEY.substring(0, 20) + '...');
}

// WebSocket configuration
export const WS_CONFIG = {
  enabled: isProd, // Only enable WebSocket in production
  host: import.meta.env.VITE_WS_HOST || (isProd 
    ? (typeof window !== 'undefined' ? window.location.hostname : 'localhost')
    : 'localhost'),
  port: import.meta.env.VITE_WS_PORT || (isProd 
    ? (typeof window !== 'undefined' && window.location.protocol === 'https:' ? '443' : '80')
    : '3001'),
  protocol: isProd 
    ? (typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:')
    : 'ws:',
  getUrl: (token?: string) => {
    if (!WS_CONFIG.enabled) {
      return null;
    }
    const baseUrl = `${WS_CONFIG.protocol}//${WS_CONFIG.host}:${WS_CONFIG.port}`;
    return token ? `${baseUrl}/?token=${token}` : baseUrl;
  }
};

// Development flags
export const DEV_FLAGS = {
  ENABLE_DEBUG: import.meta.env.VITE_ENABLE_DEBUG === 'true',
  ENABLE_MOCK_DATA: import.meta.env.VITE_ENABLE_MOCK_DATA === 'true',
};

export default ENV; 
